// Right-side layer panel — RMS-faithful: folder-aware layer list (names,
// eye/lock, indent, dissolve, master divider) + the full per-type Inspector.

import { type Layer, type LayerType } from "./types"
import type { RcAsset } from "../../../../service/report-card"
import Inspector from "./Inspector"

const TYPE_ICONS: Partial<Record<LayerType, string>> = {
  header: "ti-building-skyscraper",
  student_info: "ti-user",
  subject_table: "ti-table",
  grade_key: "ti-list-letters",
  signatures: "ti-writing-sign",
  image: "ti-photo",
  text: "ti-text-size",
  "dynamic-text": "ti-variable",
  shape: "ti-shape",
  line: "ti-minus",
  attendance: "ti-calendar-check",
}

const TYPE_LABELS: Partial<Record<LayerType, string>> = {
  header: "School Header",
  student_info: "Student Info",
  subject_table: "Subject Table",
  grade_key: "Grade Key",
  signatures: "Signatures",
  image: "Image",
  text: "Text",
  "dynamic-text": "Dynamic Text",
  shape: "Shape",
  line: "Line",
  attendance: "Attendance",
}

type Props = {
  layers: Layer[]
  selectedId: string | null
  selectedIds?: Set<string>
  assets: RcAsset[]
  onSelect: (id: string | null, additive?: boolean) => void
  onDelete: (id: string) => void
  onMoveUp: (id: string) => void
  onMoveDown: (id: string) => void
  onLayerChange: (id: string, patch: Partial<Layer>) => void
  onLayersChange: (layers: Layer[]) => void
  onReplicate?: (proto: Layer) => void
}

const labelFor = (l: Layer) =>
  (l as any).name || TYPE_LABELS[l.type] || l.type.replace(/^rms_/, "").replace(/_/g, " ")

// Exact RMS layer glyphs (LAYER_ICONS), mapped from our rms_* type names.
const LAYER_GLYPH: Record<string, string> = {
  folder: "📁", image: "🖼️", text: "📝", photo: "👤", campus_logo: "🏫",
  campus_header: "🎀", marks_table: "📊", attendance_bar: "📈", grade_badge: "🏅",
  congrats: "🎉", subject_table: "📚",
}
const layerGlyph = (t: string) => LAYER_GLYPH[t.replace(/^rms_/, "")] || "◇"

export const LayerPanel = ({
  layers, selectedId, selectedIds, assets, onSelect, onDelete, onMoveUp, onMoveDown,
  onLayerChange, onLayersChange, onReplicate,
}: Props) => {
  const multi = selectedIds || new Set<string>()
  const selected = layers.find(l => l.id === selectedId) || null

  // Own layers first, then a divider, then read-only master layers (mirrors RMS).
  const ownLayers = layers.filter(l => !l._inherited)
  const masterLayers = layers.filter(l => l._inherited)
  const ordered = [...ownLayers, ...masterLayers]

  // Hide / lock a folder cascades to its members.
  const cascade = (folderId: string, patch: Partial<Layer>) => {
    onLayersChange(layers.map(l =>
      l.id === folderId || (l as any).groupId === folderId ? { ...l, ...patch } : l
    ))
  }
  // Dissolve: drop the folder row, clear groupId from its members.
  const dissolve = (folderId: string) => {
    onLayersChange(
      layers
        .filter(l => !(l.type === "folder" && l.id === folderId))
        .map(l => (l as any).groupId === folderId ? { ...l, groupId: null, isFolderAnchor: false } : l)
    )
  }

  return (
    <div className="rcd-rightpanel" style={{ width: 300, borderLeft: "1px solid #e2e8f0", display: "flex", flexDirection: "column", background: "#fff", flexShrink: 0, overflow: "hidden" }}>

      {/* Layer list header */}
      <div style={{ padding: "8px 12px", borderBottom: "1px solid #e2e8f0", fontWeight: 700, fontSize: 12, color: "#374151", flexShrink: 0 }}>
        {ownLayers.length > 0 ? `YOUR LAYERS (${ownLayers.length})` : `LAYERS (${layers.length})`}
      </div>

      {/* Layer list */}
      <div style={{ flex: "0 0 auto", maxHeight: "38vh", overflowY: "auto", minHeight: 0, borderBottom: "1px solid #e2e8f0" }}>
        {ordered.map((layer, flatIdx) => {
          const isFolder = layer.type === "folder"
          const memberCount = isFolder ? layers.filter(l => (l as any).groupId === layer.id).length : 0
          const grouped = !!(layer as any).groupId && !isFolder
          const hidden = !!(layer as any).hidden
          const locked = !!layer.locked
          const showDivider = layer._inherited && (flatIdx === 0 || !ordered[flatIdx - 1]._inherited)
          return (
            <div key={layer.id}>
              {showDivider && (
                <div style={{ padding: "4px 10px", background: "#f5f3ff", borderTop: "1px solid #ddd6fe", borderBottom: "1px solid #ddd6fe", fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "#6d28d9" }}>
                  🔒 Master layers ({masterLayers.length}) — read-only
                </div>
              )}
              <div
                onClick={e => onSelect(layer.id, e.shiftKey)}
                style={{
                  display: "flex", alignItems: "center", gap: 4,
                  padding: "5px 8px", paddingLeft: grouped ? 22 : 8,
                  borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                  background: (selectedId === layer.id || multi.has(layer.id)) ? "#ede9fe" : isFolder ? "#eef2ff" : "transparent",
                  borderLeft: grouped ? "2px solid #dfe6ff" : undefined,
                  fontSize: 11, minHeight: 30, fontWeight: isFolder ? 600 : 400,
                }}
              >
                {/* Eye toggle (RMS 👁 / 🚫) */}
                <span
                  style={{ fontSize: 11, flexShrink: 0, cursor: layer._inherited ? "default" : "pointer", opacity: layer._inherited ? 0.5 : 1, lineHeight: 1 }}
                  title={hidden ? "Show" : "Hide"}
                  onClick={e => { e.stopPropagation(); if (layer._inherited) return; isFolder ? cascade(layer.id, { hidden: !hidden }) : onLayerChange(layer.id, { hidden: !hidden } as Partial<Layer>) }}
                >{hidden ? "🚫" : "👁"}</span>
                {/* Lock toggle (RMS 🔒 / 🔓) */}
                <span
                  style={{ fontSize: 11, flexShrink: 0, cursor: layer._inherited ? "default" : "pointer", opacity: layer._inherited ? 0.5 : 1, lineHeight: 1 }}
                  title={locked ? "Unlock" : "Lock"}
                  onClick={e => { e.stopPropagation(); if (layer._inherited) return; isFolder ? cascade(layer.id, { locked: !locked }) : onLayerChange(layer.id, { locked: !locked }) }}
                >{locked ? "🔒" : "🔓"}</span>
                {/* Folder-anchor / member badge (RMS ⚓ / ·) */}
                {grouped && (
                  <span style={{ fontSize: 9, flexShrink: 0, borderRadius: 3, padding: "0 3px", fontWeight: 700, background: (layer as any).isFolderAnchor ? "#eef2ff" : "#ede9fe", color: (layer as any).isFolderAnchor ? "#2e3192" : "#6d28d9" }} title={(layer as any).isFolderAnchor ? "Folder anchor" : "Folder member"}>{(layer as any).isFolderAnchor ? "⚓" : "·"}</span>
                )}
                {/* Type / folder glyph (RMS LAYER_ICONS emoji) */}
                <span style={{ fontSize: 12, flexShrink: 0, lineHeight: 1 }}>{layerGlyph(layer.type)}</span>
                {/* Name */}
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: isFolder ? "#2e3192" : "#374151", textDecoration: hidden ? "line-through" : undefined }}>
                  {labelFor(layer)}
                  {isFolder && <span style={{ marginLeft: 4, fontSize: 9.5, color: "#2e3192", fontFamily: "monospace" }}>· {memberCount}</span>}
                  {layer._inherited && <span className="badge bg-warning-transparent ms-1" style={{ fontSize: 9 }}>master</span>}
                </span>
                {/* Reorder + delete / dissolve (own layers only) */}
                {!layer._inherited && (
                  <>
                    <i className="ti ti-arrow-up" style={{ color: "#94a3b8", cursor: "pointer", fontSize: 11 }} onClick={e => { e.stopPropagation(); onMoveUp(layer.id) }} />
                    <i className="ti ti-arrow-down" style={{ color: "#94a3b8", cursor: "pointer", fontSize: 11 }} onClick={e => { e.stopPropagation(); onMoveDown(layer.id) }} />
                    {isFolder ? (
                      <i className="ti ti-scissors" style={{ color: "#f87171", cursor: "pointer", fontSize: 12 }} title="Dissolve folder" onClick={e => { e.stopPropagation(); dissolve(layer.id) }} />
                    ) : (
                      <i className="ti ti-x" style={{ color: "#f87171", cursor: "pointer", fontSize: 12 }} onClick={e => { e.stopPropagation(); onDelete(layer.id) }} />
                    )}
                  </>
                )}
              </div>
            </div>
          )
        })}
        {layers.length === 0 && (
          <div style={{ padding: 16, color: "#94a3b8", fontSize: 11, textAlign: "center" }}>
            No layers. Click placeholders on the left to add.
          </div>
        )}
      </div>

      {/* Selected layer Inspector */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {selected ? (
          <Inspector
            layer={selected}
            layers={layers}
            assets={assets}
            onChange={patch => onLayerChange(selected.id, patch)}
            onReplicate={onReplicate}
            onDissolveFolder={dissolve}
          />
        ) : (
          <div style={{ padding: 16, color: "#94a3b8", fontSize: 11, fontStyle: "italic" }}>
            Click a layer on the canvas or in the list to edit its properties.
          </div>
        )}
      </div>
    </div>
  )
}

export default LayerPanel
